import { KestrelViewerMessageCallbackAPI } from 'features/kestrelViewer/types/KestrelViewerInterfaces'
import { fetchNearbyPhotos } from 'features/kestrelViewer/utils/kestrelApi'
import { omit } from 'lodash'

import { KVMessageDef } from './index'

/**
 * This message is sent when the orbit is placed.
 * It is used to score the 2D photos in how the contribute to the 3D point and set the filtered photos in the context.
 * Expects the message to have the X, Y, and Z location of the orbit
 * @returns KVMessageDef
 */
export function getOrbitPlacedMessage() {
  return {
    type: 'orbitPositionResponse',
    requiredData: ['x', 'y', 'z'],

    callback: async (
      json: Record<string, unknown>,
      kv: KestrelViewerMessageCallbackAPI
    ) => {
      const worldPosition = {
        x: json.x as number,
        y: json.y as number,
        z: json.z as number,
      }

      if (!kv.kestrelModel?.id) {
        console.warn('No kestrel model available for nearby photos API')
        return
      }

      try {
        kv.setFilteringPhotos(true)
        const photoResponses = await fetchNearbyPhotos(
          kv.kestrelModel.id,
          worldPosition,
          kv.authorizedAxios,
          kv.kestrelApiUrl
        )

        const projectionMap = new Map(
          photoResponses.map((photoResponse) => [
            photoResponse.image_name,
            {
              filename: photoResponse.image_name,
              x: photoResponse.projection.x,
              y: photoResponse.projection.y,
              originalImageWidth: photoResponse.width,
              originalImageHeight: photoResponse.height,
            },
          ])
        )

        const updatedPhotos = kv.photos.map((photo) => {
          const projection = projectionMap.get(photo.filename)
          return projection
            ? { ...photo, projection }
            : omit(photo, 'projection')
        })

        kv.setPhotos(updatedPhotos)

        const filteredPhotos = updatedPhotos.filter(
          (photo) => 'projection' in photo && photo.projection
        )
        kv.setFilteredPhotos(filteredPhotos.length > 0 ? filteredPhotos : null)
      } catch (error) {
        console.error(
          'Failed to fetch nearby photos for orbit position:',
          error
        )
        kv.setFilteredPhotos(null)
        const clearedPhotos = kv.photos.map((photo) =>
          omit(photo, 'projection')
        )
        kv.setPhotos(clearedPhotos)
      }
      kv.setFilteringPhotos(false)
    },
  } as KVMessageDef
}
